import {useRef, useState} from "react";
import {Box, Popover} from "@mui/material";
import HeightIcon from "@mui/icons-material/Height";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {ElevationApp} from "../../ElevationApp";
import {MdSelection} from "../../model/view/MdViewSettings";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {RibbonButton, RibbonGroup} from "../Ribbon";
import {findItem, removeStep} from "../../scenes/frameGrid/BlockOps";
import {deselect, emitChanged, emitRebuild} from "./helpers";

type VoidSel = Extract<MdSelection, {type: "void"}>;

// the contextual tab for a step (a void cell): its height from the outer frame line
// to the step stick, and Remove Step.
export function VoidSelection(props: {app: ElevationApp; selection: VoidSel[]}) {
    const app = props.app;
    const id = props.selection[0].id;
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const heightRef = useRef<HTMLDivElement | null>(null);

    // re-render after a layout so the step height reads the new size.
    useEventEmitter2(app.frameSpec.frameBlocksChanged);
    useEventEmitter2(app.debugLayout);

    const item = findItem(app.frameSpec, id);
    const step = app.frameSpec.stepInfo.get(id);

    // the void's own size is the step height less the band it stretches across.
    const setHeight = (value: number) => {
        if(!item || item.kind !== "void" || !item.size || !step) return;
        item.size.userSet(Math.max(0, value - step.extension), {debug: "step-height-edit"});
        emitChanged(app);
    };

    const remove = () => {
        if(!removeStep(app.frameSpec, id)) {
            app.draw.showError("Can't remove this step");
            return;
        }
        emitRebuild(app);
        deselect(app);
    };

    return (
        <>
            <RibbonGroup label="Step Height">
                <Box ref={heightRef} sx={{display: "flex", height: "100%"}}>
                    <RibbonButton
                        icon={<HeightIcon />}
                        label={step ? app.draw.formatDimension(step.height) : "—"}
                        disabled={!step}
                        onClick={() => setAnchor(heightRef.current)}
                    />
                </Box>
            </RibbonGroup>
            <RibbonGroup label="Actions">
                <RibbonButton icon={<DeleteOutlineIcon sx={{color: "error.main"}} />} label="Remove Step" onClick={remove} />
            </RibbonGroup>

            <Popover
                open={!!anchor && !!step}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            >
                {step && app.draw.renderDimension({
                    value: step.height,
                    locked: true,
                    onLockChange: () => {},
                    onDone: (value: number) => {
                        setAnchor(null);
                        setHeight(value);
                    },
                    onCancel: () => setAnchor(null),
                })}
            </Popover>
        </>
    );
}
